import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, ScopedCssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { useAtom } from 'jotai';

import { chatbotConfigState } from './state/config/ChatbotConfigState';
import { getChatbotConfig } from './utils/server/storageUtils';
import { getActiveChatbotId, getActiveTenantId } from './utils/server/authUtils';
import { useAuth } from '../context/AuthContext';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import './style/style.css';
import { TRANSLATIONS } from './utils/constants/translations';

const DEFAULT_RUNTIME_CONFIG = {
    chatbotName: '',
    primaryColor: '#172983',
    secondaryColor: '#068945',
    fontFamily: 'Roboto',
    language: 'de',
    modelType: 'fast',
    chatWallpaper: 'none',
    logoUrl: '',
    welcomeMessage: '',
    hasCompletedOnboarding: false,
    onboardingCompletedAt: null,
    setupSource: null,
};

const SYSTEM_FONTS = [
    'Arial',
    'Courier New',
    'Georgia',
    'Helvetica Neue',
    'Segoe UI',
    'System UI',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana',
];

function withDefaultWelcome(config = {}) {
    const lang = config?.language === 'en' ? 'en' : 'de';
    const defaultWelcome = (TRANSLATIONS[lang] || TRANSLATIONS.de)?.defaultWelcomeMessage || '';
    return {
        ...config,
        language: lang,
        chatbotName: config?.chatbotName || (TRANSLATIONS[lang] || TRANSLATIONS.de)?.defaultChatbotName || '',
        welcomeMessage: config?.welcomeMessage || defaultWelcome,
    };
}


function notifyConfigReady() {
    if (typeof window === 'undefined') return;
    window.__CHATBOT_CONFIG_READY__ = true;
    window.dispatchEvent(new CustomEvent('chatbot-config-ready'));
}

export default function ChatbotLayout({ children }) {
    const [queryClient] = useState(() => new QueryClient());
    const [configReloadKey, setConfigReloadKey] = useState(0);
    const [config, setConfig] = useAtom(chatbotConfigState);
    const { token } = useAuth();
    const { theme: appTheme } = useAppTheme();
    const isDark = appTheme === 'dark';
    const activeTenantId = token ? getActiveTenantId({ token }) || null : null;
    const activeChatbotId = token ? getActiveChatbotId({ token, tenantId: activeTenantId }) || null : null;

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const handleScopeChange = () => {
            setConfigReloadKey((value) => value + 1);
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadConfig = async () => {
            if (typeof window !== 'undefined') {
                window.__CHATBOT_CONFIG_READY__ = false;
            }

            if (!token) {
                if (isMounted) {
                    setConfig(withDefaultWelcome({ ...DEFAULT_RUNTIME_CONFIG }));
                }
                notifyConfigReady();
                return;
            }

            try {
                const savedConfig = await getChatbotConfig(token, activeChatbotId, activeTenantId);
                if (!isMounted) return;

                if (savedConfig) {
                    setConfig(withDefaultWelcome({ ...DEFAULT_RUNTIME_CONFIG, ...savedConfig }));
                } else {
                    setConfig(withDefaultWelcome({ ...DEFAULT_RUNTIME_CONFIG }));
                }
            } catch (error) {
                if (isMounted) {
                    setConfig(withDefaultWelcome({ ...DEFAULT_RUNTIME_CONFIG }));
                }
                console.error('Error loading config', error);
            } finally {
                if (isMounted) {
                    notifyConfigReady();
                }
            }
        };

        loadConfig();

        return () => {
            isMounted = false;
        };
    }, [activeChatbotId, activeTenantId, configReloadKey, setConfig, token]);

    useEffect(() => {
        const fontName = config.fontFamily;

        if (!fontName || SYSTEM_FONTS.includes(fontName)) return;

        const formattedName = fontName.replace(/\s+/g, '+');
        const googleFontUrl = `https://fonts.googleapis.com/css2?family=${formattedName}:wght@300;400;500;700&display=swap`;
        const linkId = 'chatbot-global-font-link';

        let link = document.getElementById(linkId);
        if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }

        link.href = googleFontUrl;
    }, [config.fontFamily]);

    const theme = useMemo(() => createTheme({
        typography: {
            fontFamily: config.fontFamily || 'Roboto',
        },
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: {
                main: config.primaryColor || '#172983',
            },
            secondary: {
                main: config.secondaryColor || '#068945',
            },
            background: {
                default: isDark ? '#121212' : '#ffffff',
                paper: isDark ? '#1e1e1e' : '#ffffff',
            },
            text: {
                primary: isDark ? '#e0e0e0' : '#212529',
                secondary: isDark ? '#a0a0a0' : '#6c757d',
            },
            divider: isDark ? '#444444' : '#949393',
        },
    }), [config, isDark]);

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
                <ScopedCssBaseline style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}>
                    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                        {children}
                    </div>
                </ScopedCssBaseline>
                <Toaster position='bottom-center' />
            </ThemeProvider>
        </QueryClientProvider>
    );
}
